import React, { useState, useEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import styles from "./GlobalEvents.module.css";
import GlobalEventsHeader from "../components/GlobalEventsHeader";
import EventsSection from "../../shared/components/EventsSection/EventsSection";
import StudentTable from "../../shared/components/Table/StudentTable";
import Pagination from "../../shared/components/Pagination/Pagination";
import AnimatedCount from "../../shared/components/AnimatedCount/AnimatedCount";
import downloading from "../../assets/gifs/downloading.gif";
import {
  useEventStudentsQueries,
  useGlobalEvents,
} from "../../store/students/studentQueries";
import useStudentsPageQueries from "../../store/students/studentQueries";
import { CircularProgress } from "@mui/material";
import UploadButton from "../../shared/components/UploadButton/UploadButton";
import { getAccountType } from "../../store/auth/selectors";
import {
  checkPermission,
  PERMISSION_TYPES,
} from "../../shared/utils/permissions";

const GlobalEvents = () => {
  const { level } = useParams();
  const location = useLocation();
  const isActive = location.pathname.startsWith("/global-events");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState({ type: null, text: "" });

  const {
    events: globalEvents,
    isLoading: isGlobalEventsLoading,
    error: globalEventsError,
    refetch: refetchGlobalEvents,
  } = useGlobalEvents(level, { skip: !isActive });

  useEffect(() => {
    if (isActive) {
      refetchGlobalEvents();
    }
  }, [isActive, level, refetchGlobalEvents]);

  const { handleExportStudentsByEvent } = useStudentsPageQueries();
  const accountType = useSelector(getAccountType);
  const canUploadToEvent = useSelector(
    (state) => state.uploadPermissions.canUploadToEvent
  );

  const checkUserPermission = (permissionType) =>
    checkPermission(accountType, permissionType);

  const canUploadStudents =
    checkUserPermission(PERMISSION_TYPES.UPLOAD_STUDENTS) && canUploadToEvent;

  const {
    eventStudents,
    totalCount: eventStudentsTotal,
    attemptedCount: eventAttemptedTotal,
    currentPage: studentCurrentPage,
    itemsPerPage: studentItemsPerPage,
    attemptFilter: studentAttemptFilter,
    isLoading: isLoadingStudents,
    error: studentsError,
    handlePageChange: handleStudentPageChange,
    handleLimitChange: handleStudentItemsPerPageChange,
    handleAttemptFilterChange: handleStudentAttemptFilterChange,
    refetch,
  } = useEventStudentsQueries(1, 100, "ALL", selectedEvent?.scheduled_events);

  const formatLevelForDisplay = (levelParam) => {
    if (!levelParam) return "Semi-Final";
    return levelParam
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join("-");
  };

  const columns = [
    { key: "name", label: "Name" },
    { key: "grade", label: "Grade" },
    { key: "school_id", label: "School ID" },
    { key: "crn_id", label: "CRN ID" },
    { key: "isd_code", label: "ISD Code" },
    { key: "phone", label: "Phone" },
    { key: "loginId", label: "Login ID" },
    { key: "examName", label: "Exam Name" },
    { key: "attempt", label: "Attempted" },
    { key: "school_name", label: "School Name" },
    { key: "event_id", label: "Event ID" },
  ];

  const handleEventClick = (event) => {
    setSelectedEvent(event);
  };

  const handleExport = async (exportFormat) => {
    if (!selectedEvent?.scheduled_events) return;

    setIsExporting(true);
    setExportMessage({ type: null, text: "" });

    try {
      const response = await handleExportStudentsByEvent(
        selectedEvent.scheduled_events,
        studentAttemptFilter,
        exportFormat,
        true
      );

      if (response?.export_url) {
        const link = document.createElement("a");
        link.href = response.export_url;
        link.setAttribute("target", "_blank");
        link.setAttribute(
          "download",
          `students_export_${
            selectedEvent.event || "data"
          }_${studentAttemptFilter}.${exportFormat}`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setExportMessage({
          type: "success",
          text: `${exportFormat.toUpperCase()} export successful`,
        });
      }
    } catch (error) {
      console.error(`Error exporting students data as ${exportFormat}:`, error);
      setExportMessage({
        type: "error",
        text: `Failed to export ${exportFormat.toUpperCase()}`,
      });
    } finally {
      setIsExporting(false);

      setTimeout(() => {
        setExportMessage({ type: null, text: "" });
      }, 3000);
    }
  };

  useEffect(() => {
    setSelectedEvent(globalEvents[0]);
  }, [globalEvents]);

  // Animation delay logic for counts (matching EventsTabContent pattern)
  const [displayTotals, setDisplayTotals] = useState({
    students: eventStudentsTotal || 0,
    attempted: eventAttemptedTotal || 0,
  });
  const countTimerRef = useRef(null);
  const isFirstRenderRef = useRef(true);

  // Create a key for when counts should reset (when event changes)
  const eventContentKey = useMemo(() => {
    if (!selectedEvent) return "no-event";
    const parts = [
      selectedEvent.event ?? "event",
      selectedEvent.start_date_time ?? "start",
      selectedEvent.end_date_time ?? "end",
      ...(selectedEvent.scheduled_events ?? []),
    ].filter(Boolean);
    return parts.join("__");
  }, [selectedEvent]);

  const [countsReady, setCountsReady] = useState(true);

  // Handle delay for animation when event changes
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    setCountsReady(false);
    if (countTimerRef.current) {
      clearTimeout(countTimerRef.current);
    }
    countTimerRef.current = setTimeout(() => {
      setCountsReady(true);
      countTimerRef.current = null;
    }, 175);
  }, [eventContentKey]);

  // Only update displayTotals when countsReady changes (not on every value change)
  useEffect(() => {
    if (!countsReady) return;
    setDisplayTotals({
      students: eventStudentsTotal || 0,
      attempted: eventAttemptedTotal || 0,
    });
  }, [countsReady, eventStudentsTotal, eventAttemptedTotal]);

  useEffect(() => {
    return () => {
      if (countTimerRef.current) {
        clearTimeout(countTimerRef.current);
      }
    };
  }, []);

  return (
    <div className={styles.studentsContainer}>
      <GlobalEventsHeader selectedLevel={formatLevelForDisplay(level)} />

      <div className={styles.eventsTabContent}>
        <div className={styles.eventsTabHeader}>
          {isGlobalEventsLoading ? (
            <div className={styles.loadingContainer}>
              <CircularProgress size={40} style={{ color: "#007BFF" }} />
              <div className={styles.loader}>Loading events...</div>
            </div>
          ) : globalEventsError ? (
            <div className={styles.errorContainer}>
              <p>Error loading events: {globalEventsError.message}</p>
            </div>
          ) : (
            <EventsSection
              events={globalEvents}
              onEventClick={handleEventClick}
              activeEvent={selectedEvent}
              refetchEventStudents={refetch}
              hideFolderTab={false}
            />
          )}
          {selectedEvent && canUploadStudents && (
            <UploadButton
              selectedEvent={selectedEvent}
              uploadType={"globalStudentsRegister"}
              onUploadSuccess={refetch}
              selectedTab={"Events"}
            />
          )}
        </div>
        {!isGlobalEventsLoading &&
          !globalEventsError &&
          globalEvents.length === 0 && (
            <div className={styles.noEventsFallback}>
              No events available for this level.
            </div>
          )}

        {selectedEvent ? (
          <>
            <div className={styles.studentsInfo}>
              <span style={{ fontWeight: "500" }}>
                Total Students: <AnimatedCount value={displayTotals.students} />{" "}
                | Total Attempted:{" "}
                <AnimatedCount value={displayTotals.attempted} />
              </span>
              <motion.div
                className={styles.tableControls}
                layout
                transition={{
                  layout: { duration: 0.45, ease: [0.25, 0.1, 0.25, 1] },
                }}
              >
                <div className={styles.exportContainer}>
                  {isExporting ? (
                    <div className={styles.exportLoading}>
                      <img
                        src={downloading}
                        alt="Loading"
                        className={styles.exportLoader}
                      />
                      <span>Exporting...</span>
                    </div>
                  ) : (
                    <button
                      className={`${styles.operationSelect} ${styles.exportOperations}`}
                      onClick={() => handleExport("csv")}
                      disabled={isExporting}
                    >
                      Export CSV
                    </button>
                  )}

                  {exportMessage.type && (
                    <div
                      className={`${styles.exportMessage} ${
                        styles[exportMessage.type]
                      }`}
                    >
                      {exportMessage.text}
                    </div>
                  )}
                </div>
                <select
                  className={`${styles.operationSelect} ${styles.bulkOperations}`}
                  value={studentAttemptFilter}
                  onChange={(e) =>
                    handleStudentAttemptFilterChange(e.target.value)
                  }
                >
                  <option value="ALL">All</option>
                  <option value="ATTEMPTED">Attempted</option>
                  <option value="NON_ATTEMPTED">Non-Attempted</option>
                </select>
                <Pagination
                  totalItems={
                    studentAttemptFilter === "ATTEMPTED"
                      ? eventAttemptedTotal
                      : studentAttemptFilter === "NON_ATTEMPTED"
                      ? eventStudentsTotal - eventAttemptedTotal
                      : eventStudentsTotal
                  }
                  currentPage={studentCurrentPage}
                  itemsPerPage={studentItemsPerPage}
                  itemsPerPageOptions={[100, 200, 500, 1000, 2000]}
                  onPageChange={handleStudentPageChange}
                  onItemsPerPageChange={handleStudentItemsPerPageChange}
                />
              </motion.div>
            </div>
            {isLoadingStudents ? (
              <div className={styles.loadingContainer}>
                <CircularProgress size={60} style={{ color: "#007BFF" }} />
                <div className={styles.loader}>Loading students...</div>
              </div>
            ) : studentsError ? (
              <div className={styles.errorContainer}>
                <p>Error loading students: {studentsError.message}</p>
              </div>
            ) : (
              <AnimatePresence mode="wait">
                <motion.div
                  key={eventContentKey}
                  className={styles.eventContentWrapper}
                  initial={{ y: 24, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 24, opacity: 0 }}
                  transition={{ duration: 0.55, ease: [0.25, 0.1, 0.25, 1] }}
                >
                  {eventStudents.length > 0 ? (
                    <StudentTable
                      data={eventStudents}
                      columns={columns}
                      selectedRows={[]}
                      onSelectAll={() => {}}
                      onSelectRow={() => {}}
                    />
                  ) : (
                    <div className={styles.errorMessage}>No students found</div>
                  )}
                </motion.div>
              </AnimatePresence>
            )}
          </>
        ) : (
          !isGlobalEventsLoading &&
          !globalEventsError &&
          globalEvents.length > 0 && (
            <div className="no-data-message">
              Please select an event to view details.
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default GlobalEvents;
